import React, { useState } from 'react';
import { Modal, Panel } from 'react-bootstrap';
import ReactEcharts from 'echarts-for-react';

import { fetchApi } from '../../Network/NetworkService';
import { notif } from '../Ui/Notification';

const AGE_BUCKETS = [1, 3, 6, 12, 18, 24];

const fmtEur = (n) => n == null ? '—' : `${n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;
const fmtEurPrecise = (n) => n == null ? '—' : `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('fr-FR') : '—';

const statusMeta = {
    single_purchase: { color: '#9E9E9E', label: 'Achat unique' },
    active: { color: '#0F9D58', label: 'Actif' },
    at_risk: { color: '#F4B400', label: 'À risque' },
    churned: { color: '#D32F2F', label: 'Churned' },
};

const channelColor = {
    google_ads: '#4285F4',
    skycall: '#F4B400',
    organic_search: '#0F9D58',
    blog: '#9C27B0',
    pharmacy: '#00BCD4',
    direct: '#9E9E9E',
    referral: '#E91E63',
};
const colorFor = (ch) => channelColor[ch] || '#607D8B';

const kpiStyle = (color) => ({
    flex: '1 1 170px',
    minWidth: 170,
    padding: 14,
    borderRadius: 8,
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderLeft: `4px solid ${color}`,
});

export default function CustomerLTV() {
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [notFound, setNotFound] = useState(false);

    const search = (e) => {
        if (e) e.preventDefault();
        const q = query.trim();
        if (!q) {
            notif.addNotification('Info', 'Saisir un email ou un ID utilisateur', 'info');
            return;
        }
        setLoading(true);
        setNotFound(false);
        setResult(null);

        const isObjectId = /^[0-9a-f]{24}$/i.test(q);
        const body = isObjectId ? { userId: q } : { email: q };

        fetchApi('/users/analytics/ltv/customer', {
            method: 'POST',
            body: JSON.stringify(body),
        })
            .then((json) => {
                setLoading(false);
                setResult(json);
            })
            .catch((err) => {
                setLoading(false);
                if (err && err.message && /not found/i.test(err.message)) {
                    setNotFound(true);
                } else {
                    notif.addNotification('Erreur', err && err.message ? err.message : String(err), 'warning');
                }
            });
    };

    const chartOption = (() => {
        if (!result || !result.observedLTV) return null;
        const data = AGE_BUCKETS.map(m => {
            const v = result.observedLTV[`m${m}`];
            return v == null ? null : v;
        });
        return {
            tooltip: { trigger: 'axis', valueFormatter: (v) => v == null ? '—' : `${Math.round(v)} €` },
            grid: { top: 24, right: 16, bottom: 24, left: 56 },
            xAxis: { type: 'category', data: AGE_BUCKETS.map(m => `M${m}`), name: 'Âge depuis 1ʳᵉ commande' },
            yAxis: { type: 'value', name: 'Revenu cumulé (€)' },
            series: [{
                type: 'line',
                smooth: true,
                connectNulls: true,
                symbolSize: 8,
                lineStyle: { width: 3 },
                itemStyle: { color: '#4285F4' },
                areaStyle: { opacity: 0.15 },
                data,
            }],
        };
    })();

    const user = result && result.user;
    const summary = result && result.summary;
    const timeline = (result && result.timeline) || [];
    const status = summary ? statusMeta[summary.status] : null;

    return (
        <Panel>
            <Modal show={loading} backdrop="static" keyboard={false} size="lg" className="modal-spinner">
                <span className="loader" />
                {loading && <p>Recherche LTV client…</p>}
            </Modal>

            <Panel.Heading>
                <h6 style={{ margin: 0 }}>LTV par client</h6>
            </Panel.Heading>

            <Panel.Body>
                <form onSubmit={search} style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                        type="text"
                        placeholder="Email ou ID utilisateur"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        style={{ flex: '1 1 320px', padding: 8, border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
                    />
                    <button
                        type="submit"
                        style={{ padding: '8px 18px', backgroundColor: '#00c1c1', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                    >
                        Rechercher
                    </button>
                </form>

                {notFound && (
                    <div style={{ padding: 16, backgroundColor: '#fff3cd', border: '1px solid #ffeeba', borderRadius: 4, color: '#856404' }}>
                        Aucun utilisateur trouvé pour « {query} ».
                    </div>
                )}

                {result && user && (
                    <>
                        <div style={{ padding: 14, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 6, marginBottom: 20 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 16 }}>
                                        {(user.FName || user.LName) ? `${user.FName || ''} ${user.LName || ''}`.trim() : user.email}
                                    </div>
                                    <div style={{ color: '#666', fontSize: 13 }}>{user.email}</div>
                                    <div style={{ color: '#999', fontSize: 12 }}>
                                        ID <code>{user._id}</code> · inscrit le {fmtDate(user.createdAt)}
                                    </div>
                                </div>
                                {status && (
                                    <div style={{
                                        padding: '6px 14px',
                                        borderRadius: 999,
                                        backgroundColor: status.color,
                                        color: '#fff',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        alignSelf: 'flex-start',
                                    }}>
                                        {status.label}
                                    </div>
                                )}
                            </div>
                        </div>

                        {!summary && (
                            <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                                Aucune commande payée pour ce client.
                            </div>
                        )}

                        {summary && (
                            <>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                                    <div style={kpiStyle('#0F9D58')}>
                                        <div style={{ fontSize: 12, color: '#666' }}>Revenu total</div>
                                        <div style={{ fontSize: 22, fontWeight: 600 }}>{fmtEurPrecise(summary.totalRevenue)}</div>
                                    </div>
                                    <div style={kpiStyle('#E91E63')}>
                                        <div style={{ fontSize: 12, color: '#666' }}>Revenu net</div>
                                        <div style={{ fontSize: 22, fontWeight: 600 }}>{fmtEurPrecise(summary.netRevenue)}</div>
                                        <div style={{ fontSize: 11, color: '#999' }}>après Stripe + médecin</div>
                                    </div>
                                    <div style={kpiStyle('#4285F4')}>
                                        <div style={{ fontSize: 12, color: '#666' }}>Consultations</div>
                                        <div style={{ fontSize: 22, fontWeight: 600 }}>{summary.consultations}</div>
                                        <div style={{ fontSize: 11, color: '#999' }}>ARPV {fmtEurPrecise(summary.arpv)}</div>
                                    </div>
                                    <div style={kpiStyle(colorFor(summary.channel))}>
                                        <div style={{ fontSize: 12, color: '#666' }}>Canal d'acquisition</div>
                                        <div style={{ fontSize: 18, fontWeight: 600 }}>{summary.channel}</div>
                                    </div>
                                    <div style={kpiStyle('#F4B400')}>
                                        <div style={{ fontSize: 12, color: '#666' }}>Dernière commande</div>
                                        <div style={{ fontSize: 18, fontWeight: 600 }}>
                                            {summary.daysSinceLast === 0 ? "aujourd'hui" : `il y a ${summary.daysSinceLast}j`}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#999' }}>{fmtDate(summary.lastPaidAt)}</div>
                                    </div>
                                    <div style={kpiStyle('#9C27B0')}>
                                        <div style={{ fontSize: 12, color: '#666' }}>Fréquence moyenne</div>
                                        <div style={{ fontSize: 18, fontWeight: 600 }}>
                                            {summary.avgDaysBetweenVisits == null ? '—' : `${summary.avgDaysBetweenVisits} j`}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#999' }}>entre consultations</div>
                                    </div>
                                </div>

                                {chartOption && (
                                    <div style={{ marginBottom: 20 }}>
                                        <h6 style={{ margin: '8px 0' }}>Revenu cumulé par âge de relation</h6>
                                        <ReactEcharts option={chartOption} style={{ height: 280 }} />
                                    </div>
                                )}

                                <div className="table-responsive">
                                    <h6 style={{ margin: '16px 0 8px' }}>Historique des commandes ({timeline.length})</h6>
                                    <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Date</th>
                                                <th>Type</th>
                                                <th>État</th>
                                                <th>Options</th>
                                                <th>Revenu</th>
                                                <th>Frais Stripe</th>
                                                <th>Rémun. médecin</th>
                                                <th>Net</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {timeline.map((t, i) => {
                                                const net = t.revenue - t.stripeFee - t.doctorPayout;
                                                return (
                                                    <tr key={t._id}>
                                                        <td>{i + 1}</td>
                                                        <td>{fmtDateTime(t.createdAt)}</td>
                                                        <td>{t.type}</td>
                                                        <td>
                                                            <span style={{
                                                                padding: '2px 8px',
                                                                borderRadius: 4,
                                                                backgroundColor: '#e3f2fd',
                                                                color: '#1565c0',
                                                                fontSize: 11,
                                                            }}>{t.state}</span>
                                                        </td>
                                                        <td style={{ fontSize: 11, color: '#666' }}>
                                                            {t.priority && <span title="Priorité">⚡ </span>}
                                                            {t.skycallVoice && <span title="Skycall voice">📞</span>}
                                                        </td>
                                                        <td>{fmtEurPrecise(t.revenue)}</td>
                                                        <td style={{ color: '#999' }}>−{fmtEurPrecise(t.stripeFee)}</td>
                                                        <td style={{ color: '#999' }}>−{fmtEurPrecise(t.doctorPayout)}</td>
                                                        <td style={{ fontWeight: 600 }}>{fmtEurPrecise(net)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div style={{ marginTop: 16, fontSize: 11, color: '#888' }}>
                                    LTV observée calculée à partir de la première commande payée ({fmtDate(summary.firstPaidAt)}),
                                    soit {summary.daysSinceFirst} jours. Les commandes <code>canceled</code> / <code>refunded</code> sont exclues.
                                    Frais Stripe : {result.assumptions ? `${(result.assumptions.stripePercent * 100).toFixed(2)} % + ${result.assumptions.stripeFlatEur.toFixed(2)} €` : 'n/a'}.
                                </div>
                            </>
                        )}
                    </>
                )}
            </Panel.Body>
        </Panel>
    );
}
